import Swal from 'sweetalert2'
import './sweetAlertStyles.css'

/**
 * Servicio personalizado para SweetAlert2
 * Configuraciones predefinidas para mantener consistencia en la UI
 */
class SweetAlertService {
  constructor() {
    // Configuración base para todos los alerts
    this.baseConfig = {
      customClass: {
        popup: 'helpmed-swal-popup',
        title: 'helpmed-swal-title',
        htmlContainer: 'helpmed-swal-content',
        confirmButton: 'helpmed-swal-confirm',
        cancelButton: 'helpmed-swal-cancel',
        icon: 'helpmed-swal-icon'
      },
      buttonsStyling: false,
      showClass: {
        popup: 'animate__animated animate__fadeInDown animate__faster'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp animate__faster'
      }
    }

    // Toast configuration
    this.toastConfig = {
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      showClass: {
        popup: 'animate__animated animate__slideInRight animate__faster'
      },
      hideClass: {
        popup: 'animate__animated animate__slideOutRight animate__faster'
      },
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    }
  }

  /**
   * Muestra un toast de error
   */
  showError(message, title = 'Error') {
    return Swal.fire({
      ...this.toastConfig,
      icon: 'error',
      title: title,
      text: message,
      background: '#FEE2E2',
      color: '#991B1B',
      iconColor: '#DC2626'
    })
  }

  /**
   * Muestra un toast de éxito
   */
  showSuccess(message, title = '¡Éxito!') {
    return Swal.fire({
      ...this.toastConfig,
      icon: 'success',
      title: title,
      text: message,
      background: '#D1FAE5',
      color: '#065F46',
      iconColor: '#10B981'
    })
  }

  /**
   * Muestra un toast de advertencia
   */
  showWarning(message, title = 'Advertencia') {
    return Swal.fire({
      ...this.toastConfig,
      icon: 'warning',
      title: title,
      text: message,
      background: '#FEF3C7',
      color: '#92400E',
      iconColor: '#F59E0B'
    })
  }

  /**
   * Muestra un toast de información
   */
  showInfo(message, title = 'Información') {
    return Swal.fire({
      ...this.toastConfig,
      icon: 'info',
      title: title,
      text: message,
      background: '#DBEAFE',
      color: '#1E40AF',
      iconColor: '#3B82F6'
    })
  }

  /**
   * Modal de error de login personalizado
   */
  showLoginError(message) {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Error de Autenticación',
      text: message,
      confirmButtonText: 'Intentar de nuevo',
      customClass: {
        ...this.baseConfig.customClass,
        confirmButton: 'bg-helpmed-blue hover:bg-primary-blue text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300'
      },
      background: '#FFFFFF',
      backdrop: 'rgba(0,0,0,0.5)',
      allowOutsideClick: false
    })
  }

  /**
   * Modal de bienvenida después del login exitoso
   */
  showWelcome(userName) {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'success',
      title: `¡Bienvenido ${userName || 'a HelpMed'}!`,
      text: 'Has iniciado sesión correctamente',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
      background: '#FFFFFF',
      backdrop: 'rgba(0,0,0,0.3)'
    })
  }

  /**
   * Modal de confirmación
   */
  showConfirmation(title, text, confirmText = 'Sí', cancelText = 'Cancelar') {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'question',
      title: title,
      text: text,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      customClass: {
        ...this.baseConfig.customClass,
        confirmButton: 'bg-helpmed-blue hover:bg-primary-blue text-white px-6 py-3 rounded-lg font-semibold mr-2',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-semibold'
      },
      reverseButtons: true
    })
  }

  /**
   * Modal de carga
   */
  showLoading(title = 'Cargando...', text = 'Por favor espera') {
    return Swal.fire({
      ...this.baseConfig,
      title: title,
      text: text,
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading()
      }
    })
  }

  /**
   * Cierra cualquier modal abierto
   */
  close() {
    Swal.close()
  }

  /**
   * Modal de input para solicitar datos
   */
  async showInput(title, inputLabel, inputPlaceholder = '', inputType = 'text') {
    const { value } = await Swal.fire({
      ...this.baseConfig,
      title: title,
      input: inputType,
      inputLabel: inputLabel,
      inputPlaceholder: inputPlaceholder,
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar',
      customClass: {
        ...this.baseConfig.customClass,
        confirmButton: 'bg-helpmed-blue hover:bg-primary-blue text-white px-6 py-3 rounded-lg font-semibold mr-2',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-semibold'
      },
      inputValidator: (value) => {
        if (!value) {
          return 'Este campo es requerido'
        }
      }
    })
    return value
  }

  /**
   * Modal con imagen
   */
  showImageModal(imageUrl, title = '') {
    return Swal.fire({
      ...this.baseConfig,
      title: title,
      imageUrl: imageUrl,
      imageAlt: title,
      confirmButtonText: 'Cerrar',
      customClass: {
        ...this.baseConfig.customClass,
        confirmButton: 'bg-helpmed-blue hover:bg-primary-blue text-white px-6 py-3 rounded-lg font-semibold'
      }
    })
  }
}

// Exportar instancia singleton
export const sweetAlert = new SweetAlertService()
export default sweetAlert